import { useEffect, useId, useRef, useState } from "react";
import { useFetcher } from "react-router";
import type { Tenant } from "@repo/shared-types";
import { tenantPath } from "@/lib/tenants";
import { toast } from "@/lib/toast";
import { Modal } from "@/components/modal";
import { ErrorNote } from "@/components/form";
import { CogMenu, MenuButton, MenuLink } from "@/components/cog-menu";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Field, FieldContent, FieldDescription, FieldLabel, FieldTitle } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import type { LeavingData } from "@/routes/space-leave";

/**
 * A space's menu, behind the cog on its feed: a page for each part of running
 * it — Members, Requests while somebody is asking, Invite, Settings — shown
 * to whoever may use it; Share, on a public space, whose address opens its
 * presentation page for anybody signed in; and Leave, which asks first. The
 * owner has no Leave: they hand the space on, from Members, before they can
 * go. A private space has no Share: its link opens for its members alone.
 *
 * Whoever answers requests sees how many are waiting on the cog itself. What
 * the menu offers is presentation; the api refuses whatever the caller may
 * not do.
 */
export function SpaceMenu({
  tenant,
  role,
  memberCount,
  requestCount,
  canManageMembers,
}: {
  tenant: Tenant;
  role: string;
  memberCount: number;
  requestCount: number;
  canManageMembers: boolean;
}) {
  const [leaving, setLeaving] = useState(false);
  const [shared, setShared] = useState<string | null>(null);
  const base = tenantPath(tenant);
  const isOwner = role === "owner";
  const asking = canManageMembers ? requestCount : 0;

  return (
    <div className="relative">
      <CogMenu label={asking > 0 ? `Space menu, ${asking} asking to join` : "Space menu"} badge={asking}>
        {(close) => (
          <>
            <MenuLink to={`${base}/members`} label="Members" count={memberCount} />
            {asking > 0 && <MenuLink to={`${base}/requests`} label="Requests" count={asking} highlight />}
            {canManageMembers && <MenuLink to={`${base}/invite`} label="Invite" />}
            {isOwner && <MenuLink to={`${base}/settings`} label="Settings" />}
            {(tenant.visibility === "public" || !isOwner) && <DropdownMenuSeparator />}
            {tenant.visibility === "public" && (
              <MenuButton
                onClick={async () => {
                  close();
                  setShared(await shareSpace(tenant));
                }}
              >
                Share
              </MenuButton>
            )}
            {!isOwner && (
              <MenuButton
                danger
                onClick={() => {
                  close();
                  setLeaving(true);
                }}
              >
                Leave…
              </MenuButton>
            )}
          </>
        )}
      </CogMenu>

      {leaving && <LeaveDialog tenant={tenant} onClose={() => setLeaving(false)} />}
      {shared && <LinkSheet tenant={tenant} url={shared} onClose={() => setShared(null)} />}
    </div>
  );
}

/**
 * The space's address, through the device's share sheet where it has one,
 * or copied — which the toast says, as it says every save (decided with
 * Tiago 12 October). Answers the address when it could not be copied, for
 * the menu to show in a sheet; nothing otherwise, a dismissed sheet included.
 */
async function shareSpace(tenant: Tenant): Promise<string | null> {
  const url = new URL(tenantPath(tenant), window.location.origin).toString();
  if (typeof navigator.share === "function") {
    try {
      await navigator.share({ title: tenant.name, url });
    } catch {
      // Dismissed, or refused: nothing to say.
    }
    return null;
  }
  if (!(await copyLink(url))) return url;
  toast("Link copied.");
  return null;
}

/**
 * Copies the address: through the clipboard where the browser offers it,
 * else the older way. Over plain http — staging, or a phone on the dev
 * server — there is no clipboard, and the older way runs before anything is
 * awaited: Safari copies only within the tap.
 */
async function copyLink(url: string): Promise<boolean> {
  if (!navigator.clipboard) return copyTheOldWay(url);
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Copies through a selection the page never shows, which works without a
 * secure context, then gives the focus back. Whether it worked is all the
 * browser says.
 */
function copyTheOldWay(text: string): boolean {
  const before = document.activeElement;
  const box = document.createElement("textarea");
  box.value = text;
  box.readOnly = true;
  // Off screen, and at 16px so Safari does not zoom in on it.
  box.style.cssText = "position:fixed;top:0;left:-9999px;font-size:16px;opacity:0";
  document.body.appendChild(box);
  const copied = copySelected(box);
  box.remove();
  if (before instanceof HTMLElement) before.focus();
  return copied;
}

/** Selects all of a box and copies it. */
function copySelected(box: HTMLInputElement | HTMLTextAreaElement): boolean {
  box.focus();
  box.select();
  box.setSelectionRange(0, box.value.length);
  try {
    return document.execCommand("copy");
  } catch {
    return false;
  }
}

/**
 * The address, when it could be neither shared nor copied: selected as the
 * sheet opens, to be copied by hand. Copy link tries again from a tap of its
 * own, copying from the sheet's own box — a box outside it would lose the
 * focus to the sheet before anything is copied — and, failing, says so.
 */
function LinkSheet({ tenant, url, onClose }: { tenant: Tenant; url: string; onClose: () => void }) {
  const link = useRef<HTMLInputElement>(null);
  const [failed, setFailed] = useState(false);
  const copy = () => {
    if (link.current && copySelected(link.current)) {
      toast("Link copied.");
      onClose();
      return;
    }
    setFailed(true);
  };
  return (
    <Modal title={`Share ${tenant.name}`} onClose={onClose}>
      <p className="text-sm text-muted-foreground">Anybody signed in can open this link and ask to join.</p>
      <Input
        ref={link}
        aria-label="Link"
        readOnly
        value={url}
        autoFocus
        onFocus={(event) => event.currentTarget.select()}
      />
      {failed && (
        <p role="status" className="text-sm text-muted-foreground">
          This browser would not copy it. The link is selected: copy it from your keyboard or by holding on it.
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="lg" onClick={onClose}>
          Done
        </Button>
        <Button type="button" size="lg" onClick={copy}>
          Copy link
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Leaving, asked first. The caller's results stay in their log whatever they
 * choose; with the box ticked they come off this space's boards as they go.
 * The box is there only when something is shared, counted by the api when
 * the dialog opens.
 */
function LeaveDialog({ tenant, onClose }: { tenant: Tenant; onClose: () => void }) {
  const base = tenantPath(tenant);
  const count = useFetcher<LeavingData>();
  const leave = useFetcher<{ error?: string }>();
  const asked = useRef(false);
  const unshareId = useId();
  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    count.load(`${base}/leave`);
  }, [count, base]);

  const shared = count.data?.sharedResults;
  const busy = leave.state !== "idle";

  return (
    <Modal title={`Leave ${tenant.name}?`} onClose={onClose}>
      <leave.Form method="post" action={`${base}/leave`} className="space-y-4">
        <input type="hidden" name="name" value={tenant.name} />
        <p className="text-sm text-muted-foreground">
          Your results stay in your log, whatever you choose here. To come back you will need an
          invitation{tenant.visibility === "public" ? ", or to ask to join again" : ""}.
        </p>

        {shared === undefined ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner aria-hidden="true" />
            Counting your results shared here…
          </p>
        ) : shared === null ? (
          <ErrorNote message="Could not count your results shared here. Leaving keeps them shared." />
        ) : shared > 0 ? (
          // Unticked every time it is asked. Inside the form, so the
          // checkbox sends `unshare=on` with it when ticked.
          <FieldLabel htmlFor={unshareId}>
            <Field orientation="horizontal">
              <Checkbox id={unshareId} name="unshare" />
              <FieldContent>
                <FieldTitle>
                  Stop sharing my {shared === 1 ? "result" : `${shared.toLocaleString()} results`} here
                </FieldTitle>
                <FieldDescription>
                  {shared === 1 ? "It comes" : "They come"} off {tenant.name}'s leaderboards and{" "}
                  {shared === 1 ? "stays" : "stay"} in your log, private.
                </FieldDescription>
              </FieldContent>
            </Field>
          </FieldLabel>
        ) : null}

        {leave.data?.error && <ErrorNote message={leave.data.error} />}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="destructive" size="lg" disabled={busy || shared === undefined}>
            Leave
          </Button>
        </div>
      </leave.Form>
    </Modal>
  );
}
